import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import ContactFormPostbariatrica from '../components/ContactFormPostbariatrica';
import { useLanguage } from '../i18n/pageTranslations';

const CirugiaPostbariatrica = () => {
  const [showForm, setShowForm] = useState(false);
  const sectionRefs = useRef([]);
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Plástica Avanzada',
      heroTitle1: 'La nueva silueta que',
      heroTitle2: 'su esfuerzo merece',
      heroDesc: 'Tras una pérdida de peso significativa, el organismo conserva excesos de piel y tejido que ningún ejercicio puede eliminar. La cirugía postbariátrica restituye la armonía corporal y completa la transformación iniciada con la cirugía bariátrica.',
      requestEval: 'Solicitar valoración',
      resultsEyebrow: 'Resultados — Antes y Después',
      resultsNote: 'Caso real — Paciente del Dr. Norro',
      resultsCaption: 'Reducción de más de 40 kg — Resultado a 18 meses',
      stats: [
        { value: '+35', label: 'Años de experiencia clínica' },
        { value: '6', label: 'Procedimientos corporales disponibles' },
        { value: '18', label: 'Meses mínimos post-bariátrica recomendados' },
        { value: '100%', label: 'Plan quirúrgico individualizado' },
      ],
      contextEyebrow: '01 — Contexto',
      contextTitle1: 'Más allá del',
      contextTitle2: 'peso perdido',
      contextP1: 'La pérdida masiva de peso —ya sea mediante cirugía bariátrica o por medios no quirúrgicos— es un logro extraordinario que transforma la salud metabólica del paciente. Sin embargo, la piel y los tejidos blandos, una vez distendidos durante años, no recuperan su elasticidad de forma espontánea.',
      contextP2: 'El resultado son pliegues cutáneos redundantes en abdomen, flancos, muslos, brazos y mamas que generan maceración, infecciones cutáneas repetidas, limitación funcional y un importante impacto psicológico. El paciente que ha completado un proceso bariátrico exitoso merece una segunda etapa que le devuelva la silueta que su esfuerzo ha ganado.',
      contextP3: 'La cirugía plástica postbariátrica aborda de forma integral todos los compartimentos afectados. Mediante un plan quirúrgico secuenciado y adaptado a cada individuo, el Dr. Norro diseña una estrategia que optimiza seguridad, resultados estéticos y recuperación funcional, respetando en todo momento el estado nutricional y el equilibrio sistémico del paciente.',
      proceduresEyebrow: '02 — Procedimientos',
      proceduresTitle: 'Técnicas disponibles',
      proceduresDesc: 'Cada intervención se planifica en función del grado de ptosis, la calidad cutánea y los objetivos funcionales y estéticos del paciente.',
      procedures: [
        { num: '01', titulo: 'Dermolipectomía abdominal', descripcion: 'Resección del delantal abdominal y plicatura de la musculatura abdominal cuando existe diástasis. La técnica se adapta al grado de ptosis y la distribución del exceso tisular, con cicatriz baja y horizontal.', tags: ['Abdominoplastia', 'Panniculectomía'] },
        { num: '02', titulo: 'Body lift inferior', descripcion: 'Resección circunferencial del exceso de piel en la zona inferior del tronco, incluyendo abdomen, flancos, glúteos y zona lumbar. Indicado en casos de ptosis severa 360°.', tags: ['Lifting circumferencial'] },
        { num: '03', titulo: 'Braquioplastia', descripcion: 'Resección del exceso cutáneo de la cara interna del brazo. Permite recuperar contorno y funcionalidad del miembro superior, eliminando el característico "ala de murciélago" postbariátrico.', tags: ['Brazo', 'Axila'] },
        { num: '04', titulo: 'Lifting de muslos', descripcion: 'Corrección de la piel redundante en cara interna y/o anterolateral del muslo. Mejora el contorno, la higiene íntima y la calidad de vida al eliminar la fricción intercrural.', tags: ['Cruroplastia', 'Thigh lift'] },
        { num: '05', titulo: 'Mastopexia y aumento mamario', descripcion: 'La pérdida de peso produce ptosis y deflación mamaria en la mujer. La mastopexia reposiciona el complejo areola-pezón y puede combinarse con implantes o autoaumento para restaurar el volumen perdido.', tags: ['Ginecomastia en varones'] },
        { num: '06', titulo: 'Lifting facial y cervical', descripcion: 'La ptosis del tercio inferior facial y el exceso de piel cervical son frecuentes tras pérdidas ponderales importantes. Se aborda mediante ritidectomía y, en casos menos pronunciados, liposucción cervical con endolaser.', tags: ['Cuello', 'Papada', 'Óvalo facial'] },
      ],
      methodologyEyebrow: '03 — Metodología',
      methodologyTitle: 'Un proceso clínico estructurado',
      methodology: [
        { num: '01', titulo: 'Primera consulta y evaluación integral', descripcion: 'Exploración clínica completa, análisis del historial bariátrico, IMC actual y evolución ponderal. Se evalúa el estado nutricional (proteínas, hierro, vitaminas) y la estabilidad del peso mínimo de 12–18 meses.' },
        { num: '02', titulo: 'Planificación quirúrgica secuenciada', descripcion: 'Diseño del plan por etapas cuando se combinan múltiples zonas: el tronco se aborda primero, seguido de extremidades y finalmente la región cefálica. Se estiman tiempos quirúrgicos y protocolos anestésicos adaptados.' },
        { num: '03', titulo: 'Intervención y hospitalización', descripcion: 'Cirugía bajo anestesia general o sedación consciente según el caso. Hospitalización adaptada a la complejidad del procedimiento. Protocolo de tromboprofilaxis y antibioterapia perioperatoria sistematizado.' },
        { num: '04', titulo: 'Seguimiento y resultado definitivo', descripcion: 'Controles postoperatorios protocolizados a 1, 3, 6 y 12 meses. El resultado definitivo se valora a partir del año de la intervención, una vez completada la maduración cicatricial.' },
      ],
      quote: '"La transformación postbariátrica no termina con la báscula. Termina cuando el paciente reconoce su cuerpo como propio."',
      quoteAuthor: 'Dr. Ángel Norro Ruiz — Cirujano Plástico, Estético y Reparador',
      candidateEyebrow: '04 — Idoneidad',
      candidateTitle: '¿Es usted candidato?',
      candidateIntro: 'No todo paciente con pérdida de peso es candidato inmediato. La seguridad quirúrgica y la calidad del resultado dependen de que se cumplan ciertos criterios clínicos previos.',
      candidateNote: 'La valoración individualizada permite determinar el momento óptimo y la secuencia de procedimientos más adecuada para cada caso.',
      candidateImportant: 'El Dr. Norro realiza una valoración personalizada en la que se informa de forma transparente sobre las posibilidades reales, los riesgos y las limitaciones de cada caso concreto.',
      candidateCriteria: [
        'Pérdida de peso estabilizada durante un mínimo de 12–18 meses',
        'IMC igual o inferior a 32 kg/m² en el momento de la cirugía',
        'Estado nutricional óptimo: proteínas, hierro, vitamina D y B12 dentro de rangos normales',
        'No fumador activo (mínimo 6 semanas de abstinencia preoperatoria)',
        'Ausencia de patología no controlada (diabetes, HTA, coagulopatía)',
        'Expectativas realistas y motivación fundamentalmente funcional y de calidad de vida',
        'Comprensión de la naturaleza cicatricial de los procedimientos y del proceso de maduración',
      ],
      contactEyebrow: '05 — Contacto',
      contactTitle: 'Comience su valoración personalizada',
      contactDesc: 'El equipo del Dr. Norro le atenderá para estudiar su caso, resolver sus dudas y planificar la consulta de forma totalmente confidencial.',
      callClinic: 'Llamar a la clínica',
      sendQuery: 'Enviar consulta',
      finalCtaTitle: 'Complete su transformación',
      finalCtaDesc: 'Después del esfuerzo de la pérdida de peso, merece sentirse cómodo en su nuevo cuerpo. Dé el siguiente paso hacia su bienestar.',
      requestEvaluation: 'SOLICITAR VALORACIÓN',
      important: 'Importante:',
    },
    en: {
      eyebrow: 'Advanced Plastic Surgery',
      heroTitle1: 'The new silhouette that',
      heroTitle2: 'your effort deserves',
      heroDesc: 'After significant weight loss, the body retains excess skin and tissue that no exercise can eliminate. Post-bariatric surgery restores body harmony and completes the transformation initiated with bariatric surgery.',
      requestEval: 'Request evaluation',
      resultsEyebrow: 'Results — Before and After',
      resultsNote: 'Real case — Dr. Norro\'s patient',
      resultsCaption: 'Reduction of more than 40 kg — Result at 18 months',
      stats: [
        { value: '+35', label: 'Years of clinical experience' },
        { value: '6', label: 'Body procedures available' },
        { value: '18', label: 'Minimum months post-bariatric recommended' },
        { value: '100%', label: 'Individualized surgical plan' },
      ],
      contextEyebrow: '01 — Context',
      contextTitle1: 'Beyond the',
      contextTitle2: 'weight lost',
      contextP1: 'Massive weight loss —whether through bariatric surgery or non-surgical means— is an extraordinary achievement that transforms the patient\'s metabolic health. However, skin and soft tissues, once stretched for years, do not spontaneously recover their elasticity.',
      contextP2: 'The result is redundant skin folds in the abdomen, flanks, thighs, arms and breasts that generate maceration, repeated skin infections, functional limitation and significant psychological impact. The patient who has completed a successful bariatric process deserves a second stage that restores the silhouette their effort has earned.',
      contextP3: 'Post-bariatric plastic surgery comprehensively addresses all affected compartments. Through a sequenced surgical plan adapted to each individual, Dr. Norro designs a strategy that optimizes safety, aesthetic results and functional recovery, always respecting the patient\'s nutritional status and systemic balance.',
      proceduresEyebrow: '02 — Procedures',
      proceduresTitle: 'Available techniques',
      proceduresDesc: 'Each intervention is planned based on the degree of ptosis, skin quality and the patient\'s functional and aesthetic goals.',
      procedures: [
        { num: '01', titulo: 'Abdominal dermolipectomy', descripcion: 'Resection of the abdominal apron and plication of the abdominal musculature when diastasis exists. The technique adapts to the degree of ptosis and the distribution of excess tissue, with a low horizontal scar.', tags: ['Abdominoplasty', 'Panniculectomy'] },
        { num: '02', titulo: 'Lower body lift', descripcion: 'Circumferential resection of excess skin in the lower trunk area, including abdomen, flanks, buttocks and lumbar area. Indicated in cases of severe 360° ptosis.', tags: ['Circumferential lifting'] },
        { num: '03', titulo: 'Brachioplasty', descripcion: 'Resection of excess skin from the inner arm. It allows recovery of contour and functionality of the upper limb, eliminating the characteristic post-bariatric "bat wing".', tags: ['Arm', 'Armpit'] },
        { num: '04', titulo: 'Thigh lift', descripcion: 'Correction of redundant skin on the inner and/or anterolateral thigh. Improves contour, intimate hygiene and quality of life by eliminating intercrural friction.', tags: ['Cruroplasty', 'Thigh lift'] },
        { num: '05', titulo: 'Breast lift and augmentation', descripcion: 'Weight loss produces breast ptosis and deflation in women. Mastopexy repositions the areola-nipple complex and can be combined with implants or auto-augmentation to restore lost volume.', tags: ['Gynecomastia in men'] },
        { num: '06', titulo: 'Facial and cervical lift', descripcion: 'Lower facial third ptosis and excess cervical skin are common after significant weight loss. It is addressed through rhytidectomy and, in less pronounced cases, cervical liposuction with endolaser.', tags: ['Neck', 'Double chin', 'Facial oval'] },
      ],
      methodologyEyebrow: '03 — Methodology',
      methodologyTitle: 'A structured clinical process',
      methodology: [
        { num: '01', titulo: 'First consultation and comprehensive evaluation', descripcion: 'Complete clinical examination, analysis of bariatric history, current BMI and weight evolution. Nutritional status (proteins, iron, vitamins) and weight stability of at least 12-18 months are evaluated.' },
        { num: '02', titulo: 'Sequenced surgical planning', descripcion: 'Design of the staged plan when combining multiple areas: the trunk is addressed first, followed by extremities and finally the cephalic region. Surgical times and adapted anesthetic protocols are estimated.' },
        { num: '03', titulo: 'Surgery and hospitalization', descripcion: 'Surgery under general anesthesia or conscious sedation depending on the case. Hospitalization adapted to the complexity of the procedure. Systematized thromboprophylaxis and perioperative antibiotic therapy protocol.' },
        { num: '04', titulo: 'Follow-up and final result', descripcion: 'Protocolized postoperative controls at 1, 3, 6 and 12 months. The final result is assessed from one year after the intervention, once scar maturation is complete.' },
      ],
      quote: '"Post-bariatric transformation does not end with the scale. It ends when the patient recognizes their body as their own."',
      quoteAuthor: 'Dr. Ángel Norro Ruiz — Plastic, Aesthetic and Reconstructive Surgeon',
      candidateEyebrow: '04 — Suitability',
      candidateTitle: 'Are you a candidate?',
      candidateIntro: 'Not every patient with weight loss is an immediate candidate. Surgical safety and quality of results depend on meeting certain prior clinical criteria.',
      candidateNote: 'Individualized assessment allows determining the optimal timing and the most appropriate sequence of procedures for each case.',
      candidateImportant: 'Dr. Norro performs a personalized assessment in which he transparently informs about the real possibilities, risks and limitations of each specific case.',
      candidateCriteria: [
        'Stabilized weight loss for a minimum of 12-18 months',
        'BMI equal to or less than 32 kg/m² at the time of surgery',
        'Optimal nutritional status: proteins, iron, vitamin D and B12 within normal ranges',
        'Non-active smoker (minimum 6 weeks of preoperative abstinence)',
        'Absence of uncontrolled pathology (diabetes, hypertension, coagulopathy)',
        'Realistic expectations and primarily functional and quality of life motivation',
        'Understanding of the scarring nature of procedures and the maturation process',
      ],
      contactEyebrow: '05 — Contact',
      contactTitle: 'Start your personalized evaluation',
      contactDesc: 'Dr. Norro\'s team will assist you to study your case, resolve your doubts and plan your consultation in a completely confidential manner.',
      callClinic: 'Call the clinic',
      sendQuery: 'Send inquiry',
      finalCtaTitle: 'Complete your transformation',
      finalCtaDesc: 'After the effort of weight loss, you deserve to feel comfortable in your new body. Take the next step towards your well-being.',
      requestEvaluation: 'REQUEST EVALUATION',
      important: 'Important:',
    },
    fr: {
      eyebrow: 'Chirurgie Plastique Avancée',
      heroTitle1: 'La nouvelle silhouette que',
      heroTitle2: 'votre effort mérite',
      heroDesc: 'Après une perte de poids significative, l\'organisme conserve des excès de peau et de tissu qu\'aucun exercice ne peut éliminer. La chirurgie post-bariatrique restaure l\'harmonie corporelle et complète la transformation initiée avec la chirurgie bariatrique.',
      requestEval: 'Demander une évaluation',
      resultsEyebrow: 'Résultats — Avant et Après',
      resultsNote: 'Cas réel — Patient du Dr. Norro',
      resultsCaption: 'Réduction de plus de 40 kg — Résultat à 18 mois',
      stats: [
        { value: '+35', label: 'Années d\'expérience clinique' },
        { value: '6', label: 'Procédures corporelles disponibles' },
        { value: '18', label: 'Mois minimum post-bariatrique recommandés' },
        { value: '100%', label: 'Plan chirurgical individualisé' },
      ],
      contextEyebrow: '01 — Contexte',
      contextTitle1: 'Au-delà du',
      contextTitle2: 'poids perdu',
      contextP1: 'La perte de poids massive —que ce soit par chirurgie bariatrique ou par des moyens non chirurgicaux— est une réalisation extraordinaire qui transforme la santé métabolique du patient. Cependant, la peau et les tissus mous, une fois distendus pendant des années, ne retrouvent pas spontanément leur élasticité.',
      contextP2: 'Le résultat est des plis cutanés redondants au niveau de l\'abdomen, des flancs, des cuisses, des bras et des seins qui génèrent macération, infections cutanées répétées, limitation fonctionnelle et impact psychologique important. Le patient qui a complété un processus bariatrique réussi mérite une seconde étape qui lui rende la silhouette que son effort a gagnée.',
      contextP3: 'La chirurgie plastique post-bariatrique aborde de manière globale tous les compartiments affectés. Grâce à un plan chirurgical séquencé et adapté à chaque individu, le Dr. Norro conçoit une stratégie qui optimise la sécurité, les résultats esthétiques et la récupération fonctionnelle, respectant toujours l\'état nutritionnel et l\'équilibre systémique du patient.',
      proceduresEyebrow: '02 — Procédures',
      proceduresTitle: 'Techniques disponibles',
      proceduresDesc: 'Chaque intervention est planifiée en fonction du degré de ptose, de la qualité cutanée et des objectifs fonctionnels et esthétiques du patient.',
      procedures: [
        { num: '01', titulo: 'Dermolipectomie abdominale', descripcion: 'Résection du tablier abdominal et plicature de la musculature abdominale en cas de diastasis. La technique s\'adapte au degré de ptose et à la distribution de l\'excès tissulaire, avec une cicatrice basse et horizontale.', tags: ['Abdominoplastie', 'Panniculectomie'] },
        { num: '02', titulo: 'Body lift inférieur', descripcion: 'Résection circonférentielle de l\'excès de peau dans la zone inférieure du tronc, incluant abdomen, flancs, fesses et zone lombaire. Indiqué en cas de ptose sévère à 360°.', tags: ['Lifting circonférentiel'] },
        { num: '03', titulo: 'Brachioplastie', descripcion: 'Résection de l\'excès cutané de la face interne du bras. Permet de récupérer le contour et la fonctionnalité du membre supérieur, éliminant l\'"aile de chauve-souris" caractéristique post-bariatrique.', tags: ['Bras', 'Aisselle'] },
        { num: '04', titulo: 'Lifting des cuisses', descripcion: 'Correction de la peau redondante sur la face interne et/ou antérolatérale de la cuisse. Améliore le contour, l\'hygiène intime et la qualité de vie en éliminant la friction intercrurale.', tags: ['Cruroplastie', 'Thigh lift'] },
        { num: '05', titulo: 'Mastopexie et augmentation mammaire', descripcion: 'La perte de poids produit une ptose et une déflation mammaire chez la femme. La mastopexie repositionne le complexe aréole-mamelon et peut être combinée avec des implants ou une auto-augmentation pour restaurer le volume perdu.', tags: ['Gynécomastie chez l\'homme'] },
        { num: '06', titulo: 'Lifting facial et cervical', descripcion: 'La ptose du tiers inférieur du visage et l\'excès de peau cervicale sont fréquents après des pertes de poids importantes. Il est traité par rhytidectomie et, dans les cas moins prononcés, par liposuccion cervicale avec endolaser.', tags: ['Cou', 'Double menton', 'Ovale facial'] },
      ],
      methodologyEyebrow: '03 — Méthodologie',
      methodologyTitle: 'Un processus clinique structuré',
      methodology: [
        { num: '01', titulo: 'Première consultation et évaluation intégrale', descripcion: 'Examen clinique complet, analyse de l\'historique bariatrique, IMC actuel et évolution pondérale. L\'état nutritionnel (protéines, fer, vitamines) et la stabilité du poids minimum de 12-18 mois sont évalués.' },
        { num: '02', titulo: 'Planification chirurgicale séquencée', descripcion: 'Conception du plan par étapes lors de la combinaison de plusieurs zones: le tronc est abordé en premier, suivi des extrémités et enfin de la région céphalique. Les temps chirurgicaux et les protocoles anesthésiques adaptés sont estimés.' },
        { num: '03', titulo: 'Intervention et hospitalisation', descripcion: 'Chirurgie sous anesthésie générale ou sédation consciente selon le cas. Hospitalisation adaptée à la complexité de la procédure. Protocole de thromboprophylaxie et d\'antibiothérapie périopératoire systématisé.' },
        { num: '04', titulo: 'Suivi et résultat définitif', descripcion: 'Contrôles postopératoires protocolisés à 1, 3, 6 et 12 mois. Le résultat définitif est évalué à partir d\'un an après l\'intervention, une fois la maturation cicatricielle terminée.' },
      ],
      quote: '"La transformation post-bariatrique ne se termine pas avec la balance. Elle se termine quand le patient reconnaît son corps comme le sien."',
      quoteAuthor: 'Dr. Ángel Norro Ruiz — Chirurgien Plastique, Esthétique et Réparateur',
      candidateEyebrow: '04 — Aptitude',
      candidateTitle: 'Êtes-vous candidat?',
      candidateIntro: 'Tous les patients ayant perdu du poids ne sont pas candidats immédiats. La sécurité chirurgicale et la qualité du résultat dépendent du respect de certains critères cliniques préalables.',
      candidateNote: 'L\'évaluation individualisée permet de déterminer le moment optimal et la séquence de procédures la plus appropriée pour chaque cas.',
      candidateImportant: 'Le Dr. Norro réalise une évaluation personnalisée dans laquelle il informe de manière transparente sur les possibilités réelles, les risques et les limites de chaque cas spécifique.',
      candidateCriteria: [
        'Perte de poids stabilisée pendant un minimum de 12-18 mois',
        'IMC égal ou inférieur à 32 kg/m² au moment de la chirurgie',
        'État nutritionnel optimal: protéines, fer, vitamine D et B12 dans les plages normales',
        'Non-fumeur actif (minimum 6 semaines d\'abstinence préopératoire)',
        'Absence de pathologie non contrôlée (diabète, HTA, coagulopathie)',
        'Attentes réalistes et motivation principalement fonctionnelle et de qualité de vie',
        'Compréhension de la nature cicatricielle des procédures et du processus de maturation',
      ],
      contactEyebrow: '05 — Contact',
      contactTitle: 'Commencez votre évaluation personnalisée',
      contactDesc: 'L\'équipe du Dr. Norro vous assistera pour étudier votre cas, résoudre vos doutes et planifier la consultation de manière totalement confidentielle.',
      callClinic: 'Appeler la clinique',
      sendQuery: 'Envoyer une demande',
      finalCtaTitle: 'Complétez votre transformation',
      finalCtaDesc: 'Après l\'effort de la perte de poids, vous méritez de vous sentir à l\'aise dans votre nouveau corps. Faites le pas suivant vers votre bien-être.',
      requestEvaluation: 'DEMANDER UNE ÉVALUATION',
      important: 'Important:',
    },
    pt: {
      eyebrow: 'Cirurgia Plástica Avançada',
      heroTitle1: 'A nova silhueta que',
      heroTitle2: 'seu esforço merece',
      heroDesc: 'Após uma perda de peso significativa, o organismo conserva excessos de pele e tecido que nenhum exercício pode eliminar. A cirurgia pós-bariátrica restitui a harmonia corporal e completa a transformação iniciada com a cirurgia bariátrica.',
      requestEval: 'Solicitar avaliação',
      resultsEyebrow: 'Resultados — Antes e Depois',
      resultsNote: 'Caso real — Paciente do Dr. Norro',
      resultsCaption: 'Redução de mais de 40 kg — Resultado aos 18 meses',
      stats: [
        { value: '+35', label: 'Anos de experiência clínica' },
        { value: '6', label: 'Procedimentos corporais disponíveis' },
        { value: '18', label: 'Meses mínimos pós-bariátrica recomendados' },
        { value: '100%', label: 'Plano cirúrgico individualizado' },
      ],
      contextEyebrow: '01 — Contexto',
      contextTitle1: 'Além do',
      contextTitle2: 'peso perdido',
      contextP1: 'A perda massiva de peso —seja por cirurgia bariátrica ou por meios não cirúrgicos— é uma conquista extraordinária que transforma a saúde metabólica do paciente. No entanto, a pele e os tecidos moles, uma vez distendidos durante anos, não recuperam sua elasticidade de forma espontânea.',
      contextP2: 'O resultado são dobras cutâneas redundantes no abdômen, flancos, coxas, braços e mamas que geram maceração, infecções cutâneas repetidas, limitação funcional e um importante impacto psicológico. O paciente que completou um processo bariátrico bem-sucedido merece uma segunda etapa que lhe devolva a silhueta que seu esforço conquistou.',
      contextP3: 'A cirurgia plástica pós-bariátrica aborda de forma integral todos os compartimentos afetados. Através de um plano cirúrgico sequenciado e adaptado a cada indivíduo, o Dr. Norro projeta uma estratégia que otimiza segurança, resultados estéticos e recuperação funcional, respeitando sempre o estado nutricional e o equilíbrio sistêmico do paciente.',
      proceduresEyebrow: '02 — Procedimentos',
      proceduresTitle: 'Técnicas disponíveis',
      proceduresDesc: 'Cada intervenção é planejada em função do grau de ptose, qualidade cutânea e objetivos funcionais e estéticos do paciente.',
      procedures: [
        { num: '01', titulo: 'Dermolipectomia abdominal', descripcion: 'Ressecção do avental abdominal e plicatura da musculatura abdominal quando existe diástase. A técnica se adapta ao grau de ptose e à distribuição do excesso tecidual, com cicatriz baixa e horizontal.', tags: ['Abdominoplastia', 'Panniculectomia'] },
        { num: '02', titulo: 'Body lift inferior', descripcion: 'Ressecção circunferencial do excesso de pele na zona inferior do tronco, incluindo abdômen, flancos, glúteos e zona lombar. Indicado em casos de ptose severa 360°.', tags: ['Lifting circunferencial'] },
        { num: '03', titulo: 'Braquioplastia', descripcion: 'Ressecção do excesso cutâneo da face interna do braço. Permite recuperar contorno e funcionalidade do membro superior, eliminando a característica "asa de morcego" pós-bariátrica.', tags: ['Braço', 'Axila'] },
        { num: '04', titulo: 'Lifting de coxas', descripcion: 'Correção da pele redundante na face interna e/ou anterolateral da coxa. Melhora o contorno, a higiene íntima e a qualidade de vida ao eliminar a fricção intercrural.', tags: ['Cruroplastia', 'Thigh lift'] },
        { num: '05', titulo: 'Mastopexia e aumento mamário', descripcion: 'A perda de peso produz ptose e deflação mamária na mulher. A mastopexia reposiciona o complexo aréola-mamilo e pode ser combinada com implantes ou autoaumento para restaurar o volume perdido.', tags: ['Ginecomastia em homens'] },
        { num: '06', titulo: 'Lifting facial e cervical', descripcion: 'A ptose do terço inferior facial e o excesso de pele cervical são frequentes após perdas de peso importantes. É abordado mediante ritidectomia e, em casos menos pronunciados, lipoaspiração cervical com endolaser.', tags: ['Pescoço', 'Papada', 'Oval facial'] },
      ],
      methodologyEyebrow: '03 — Metodologia',
      methodologyTitle: 'Um processo clínico estruturado',
      methodology: [
        { num: '01', titulo: 'Primeira consulta e avaliação integral', descripcion: 'Exame clínico completo, análise do histórico bariátrico, IMC atual e evolução de peso. Avalia-se o estado nutricional (proteínas, ferro, vitaminas) e a estabilidade do peso mínimo de 12-18 meses.' },
        { num: '02', titulo: 'Planejamento cirúrgico sequenciado', descripcion: 'Desenho do plano por etapas quando se combinam múltiplas zonas: o tronco é abordado primeiro, seguido de extremidades e finalmente a região cefálica. Estimam-se tempos cirúrgicos e protocolos anestésicos adaptados.' },
        { num: '03', titulo: 'Intervenção e hospitalização', descripcion: 'Cirurgia sob anestesia geral ou sedação consciente segundo o caso. Hospitalização adaptada à complexidade do procedimento. Protocolo de tromboprofilaxia e antibioticoterapia perioperatória sistematizado.' },
        { num: '04', titulo: 'Acompanhamento e resultado definitivo', descripcion: 'Controles pós-operatórios protocolizados a 1, 3, 6 e 12 meses. O resultado definitivo é avaliado a partir de um ano da intervenção, uma vez completada a maturação cicatricial.' },
      ],
      quote: '"A transformação pós-bariátrica não termina com a balança. Termina quando o paciente reconhece seu corpo como próprio."',
      quoteAuthor: 'Dr. Ángel Norro Ruiz — Cirurgião Plástico, Estético e Reparador',
      candidateEyebrow: '04 — Adequação',
      candidateTitle: 'Você é candidato?',
      candidateIntro: 'Nem todo paciente com perda de peso é candidato imediato. A segurança cirúrgica e a qualidade do resultado dependem de que sejam cumpridos certos critérios clínicos prévios.',
      candidateNote: 'A avaliação individualizada permite determinar o momento ideal e a sequência de procedimentos mais adequada para cada caso.',
      candidateImportant: 'O Dr. Norro realiza uma avaliação personalizada na qual informa de forma transparente sobre as possibilidades reais, os riscos e as limitações de cada caso concreto.',
      candidateCriteria: [
        'Perda de peso estabilizada durante um mínimo de 12-18 meses',
        'IMC igual ou inferior a 32 kg/m² no momento da cirurgia',
        'Estado nutricional ótimo: proteínas, ferro, vitamina D e B12 dentro das faixas normais',
        'Não fumante ativo (mínimo 6 semanas de abstinência pré-operatória)',
        'Ausência de patologia não controlada (diabetes, HAS, coagulopatia)',
        'Expectativas realistas e motivação fundamentalmente funcional e de qualidade de vida',
        'Compreensão da natureza cicatricial dos procedimentos e do processo de maturação',
      ],
      contactEyebrow: '05 — Contato',
      contactTitle: 'Inicie sua avaliação personalizada',
      contactDesc: 'A equipe do Dr. Norro atenderá você para estudar seu caso, resolver suas dúvidas e planejar a consulta de forma totalmente confidencial.',
      callClinic: 'Ligar para a clínica',
      sendQuery: 'Enviar consulta',
      finalCtaTitle: 'Complete sua transformação',
      finalCtaDesc: 'Após o esforço da perda de peso, você merece se sentir confortável em seu novo corpo. Dê o próximo passo em direção ao seu bem-estar.',
      requestEvaluation: 'SOLICITAR AVALIAÇÃO',
      important: 'Importante:',
    },
  };

  const t = texts[lang] || texts.es;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const openForm = () => {
    setShowForm(true);
    document.body.style.overflow = 'hidden';
  };

  const closeForm = () => {
    setShowForm(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-12 pb-24 bg-ivory overflow-hidden">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center animate-on-scroll">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-8 leading-[1.1]">
              {t.heroTitle1}<br />
              <span className="text-gold">{t.heroTitle2}</span>
            </h1>
            
            <p className="text-text-secondary text-lg leading-relaxed mb-10 max-w-3xl mx-auto">
              {t.heroDesc}
            </p>
            
            <button onClick={openForm} className="btn-gold inline-flex items-center gap-2 group">
              {t.requestEval}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Before/After Results Section */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-10 animate-on-scroll">
            <p className="eyebrow mb-4">{t.resultsEyebrow}</p>
            <p className="text-text-muted text-sm">{t.resultsNote}</p>
          </div>
          
          <div className="animate-on-scroll">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-gold"></div>
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-gold"></div>
              <img src="https://customer-assets.emergentagent.com/job_reset-chat-new/artifacts/uejt70xl_collage_anonimizado.jpg" alt="Results" className="w-full h-auto" />
            </div>
            <div className="mt-6 text-center">
              <p className="text-charcoal font-serif text-xl">{t.resultsCaption}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 animate-on-scroll">
            {t.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-serif text-4xl md:text-5xl text-gold mb-2">{stat.value}</p>
                <p className="text-sm text-gold-light/70">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 01 - Contexto */}
      <section className="py-24 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="animate-on-scroll">
            <p className="eyebrow mb-6">{t.contextEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal mb-10">
              {t.contextTitle1}<br />{t.contextTitle2}
            </h2>
            <div className="w-16 h-px bg-gold mb-10"></div>
            <div className="space-y-6 text-text-secondary leading-relaxed">
              <p>{t.contextP1}</p>
              <p>{t.contextP2}</p>
              <p>{t.contextP3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 02 - Procedimientos */}
      <section className="py-24 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="mb-16 animate-on-scroll">
            <p className="eyebrow mb-6">{t.proceduresEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal mb-6">{t.proceduresTitle}</h2>
            <p className="text-text-secondary max-w-2xl">{t.proceduresDesc}</p>
          </div>
          
          <div className="grid gap-6">
            {t.procedures.map((proc, index) => (
              <div key={index} className="card-luxury p-8 animate-on-scroll">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <span className="font-serif text-5xl text-gold/20">{proc.num}</span>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-serif text-2xl text-charcoal mb-4">{proc.titulo}</h3>
                    <p className="text-text-secondary mb-6 leading-relaxed">{proc.descripcion}</p>
                    <div className="flex flex-wrap gap-2">
                      {proc.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="px-4 py-1 border border-divider text-text-muted text-xs tracking-wider uppercase">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 03 - Metodología */}
      <section className="py-24 bg-ivory">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="mb-16 animate-on-scroll">
            <p className="eyebrow mb-6">{t.methodologyEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal">{t.methodologyTitle}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {t.methodology.map((paso, index) => (
              <div key={index} className="relative pl-20 animate-on-scroll">
                <div className="absolute left-0 top-0 w-14 h-14 border border-gold flex items-center justify-center">
                  <span className="font-serif text-xl text-gold">{paso.num}</span>
                </div>
                <h3 className="font-serif text-xl text-charcoal mb-4">{paso.titulo}</h3>
                <p className="text-text-secondary leading-relaxed">{paso.descripcion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-4xl text-center animate-on-scroll">
          <div className="w-12 h-px bg-gold mx-auto mb-10"></div>
          <blockquote className="font-serif text-2xl md:text-3xl text-ivory font-light italic leading-relaxed mb-8 border-none pl-0">
            {t.quote}
          </blockquote>
          <p className="text-gold-light text-sm tracking-wider uppercase">{t.quoteAuthor}</p>
        </div>
      </section>

      {/* Section 04 - Idoneidad */}
      <section className="py-24 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="mb-12 animate-on-scroll">
            <p className="eyebrow mb-6">{t.candidateEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal mb-8">{t.candidateTitle}</h2>
          </div>
          
          <div className="bg-ivory p-10 border border-divider animate-on-scroll">
            <p className="text-text-secondary mb-6 leading-relaxed">{t.candidateIntro}</p>
            <p className="text-text-secondary mb-10 leading-relaxed">{t.candidateNote}</p>
            
            <div className="bg-cream border-l-2 border-gold p-6 mb-10">
              <p className="text-text-secondary">
                <strong className="text-charcoal">{t.important}</strong> {t.candidateImportant}
              </p>
            </div>
            
            <div className="space-y-4">
              {t.candidateCriteria.map((criterio, index) => (
                <div key={index} className="flex items-start gap-4">
                  <span className="text-gold mt-1">✦</span>
                  <p className="text-text-secondary">{criterio}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 05 - Contacto */}
      <section id="contacto" className="py-24 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl text-center animate-on-scroll">
          <p className="eyebrow mb-6">{t.contactEyebrow}</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal mb-6">{t.contactTitle}</h2>
          <p className="text-text-secondary text-lg mb-12 max-w-2xl mx-auto">{t.contactDesc}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+34952764547" className="btn-dark inline-flex items-center justify-center gap-3">
              <Phone size={18} />
              {t.callClinic}
            </a>
            <button onClick={openForm} className="btn-gold inline-flex items-center justify-center gap-3">
              <Mail size={18} />
              {t.sendQuery}
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl text-center animate-on-scroll">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal mb-6">{t.finalCtaTitle}</h3>
          <p className="text-text-secondary mb-10 max-w-2xl mx-auto">{t.finalCtaDesc}</p>
          <button onClick={openForm} className="btn-gold">{t.requestEvaluation}</button>
        </div>
      </section>

      {/* Modal de Formulario */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/80 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-full max-w-2xl my-8">
            <ContactFormPostbariatrica onClose={closeForm} />
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CirugiaPostbariatrica;
